import './formbuilder.scss';

import { FetchManager, FriendlyCaptcha, DropzoneHandler } from 'js-pimcore-formbuilder';
import { createElementFromHTML, smoothScrollTo, getOffset } from '../../utilities/js/helper';

class FormBuilder {
    constructor ($element, options) {
        const defaults = {
            initAttr: 'data-form',
            buttonDisabledClass: 'button--disabled',
            scrollOffset: 32
        };

        this.settings = Object.assign({}, defaults, options);

        this.$formBuilder = $element;
        this.$form = this.$formBuilder.querySelector('form');
        this.$submitButton = this.$form.querySelector('button[type="submit"]');
        this.$successTarget = this.$formBuilder.querySelector('[data-formbuilder="success"]');
        this.scrollOffset = this.settings.scrollOffset;

        this.$uploader = this.$form.querySelector('[data-js-handler="drop-zone"]');

        this.$header = document.querySelector('[data-siteheader="root"]');
        if (this.$header) {
            this.scrollOffset = this.scrollOffset + this.$header.offsetHeight;
        }

        if (!this.$successTarget) {
            this.$successTarget = this.$formBuilder.closest('[data-formbuilder="success"]');
        }
    }

    initialize () {
        this.$form.addEventListener('submit', () => {
            if (this.$submitButton) {
                this.BtnLoader = createElementFromHTML(window.OPTIONS.loader);
                this.$submitButton.appendChild(this.BtnLoader);
            }
        });

        if (this.$uploader) {
            this.uploaderData = this.$uploader ? JSON.parse(this.$uploader.dataset.engineOptions) : {};
            this.uploaderTranslations = this.$uploader ? JSON.parse(this.$uploader.parentNode.dataset.formbuilderformUploaderMessages) : {};

            this.initializeUploader();
        }

        new FetchManager(this.$form, { // eslint-disable-line
            submitBtnSelector: 'button[type="submit"]',
            onSuccess: (messages, redirect, form) => {
                console.log('onSuccess', messages, form);
                if (messages.length > 0) {
                    const successMessage = messages[0];

                    if (typeof successMessage.type !== 'undefined' && typeof successMessage.message !== 'undefined') {
                        if (successMessage.type === 'success') {
                            this.$successTarget.innerHTML = '';
                            this.$successTarget.appendChild(createElementFromHTML(successMessage.message));
                            this.$form.remove();

                            window.dispatchEvent(
                                new CustomEvent('content.loaded', {
                                    view: window,
                                    bubbles: true,
                                    cancelable: false,
                                    detail: {
                                        $context: this.$successTarget
                                    }
                                })
                            );

                            smoothScrollTo(0, getOffset(this.$successTarget).top - this.scrollOffset, 400);
                        }
                    }
                } else {
                    if (redirect !== '') {
                        window.location.href = redirect;
                    }
                }
            },
            onFail: (response, form) => {
                console.log(response, form);
                if (response.validation_errors) {
                    this.generateFormErrors(response.validation_errors);
                }
            },
            onGeneralError: (generalErrorMessages, form) => {
                console.log(generalErrorMessages);
            },
            onRequestDone: (form) => {
                this.$submitButton.querySelector('.loading')?.remove();
            },
            elementTransformer: {
                addValidationMessage: (form, field, messages) => {
                    const $field = field.closest('.form-field');
                    $field.classList.add('form-field--error');
                    if (messages.length > 0) {
                        this.generateFieldErrors($field, messages);
                    }
                },
                removeFormValidations: (form) => {
                    form.querySelectorAll('.form-field--error').forEach((el) => el.classList.remove('form-field--error'));
                    form.querySelectorAll('.form-errors').forEach((el) => el.remove());
                }
            }
        });
    }

    initializeUploader () {
        const formbuilderFormInstance = this; // explanation - we have to save our constructor "this" to use our defined functions
        this.$dropzone = this.$form.querySelector('[data-js-handler="drop-zone"]');

        if (this.$dropzone) {
            const $field = this.$dropzone.closest('.form-field');

            let maxFilesize = 10;
            let acceptedFiles = '.pdf,.jpg,.jpeg,.png,.heic';
            let hasError = false;
            let programaticallyRemoved = false;

            this.uploaderData = this.$dropzone ? JSON.parse(this.$dropzone.dataset.engineOptions) : {};
            this.uploaderTranslations = this.$dropzone ? JSON.parse(this.$dropzone.parentNode.dataset.formbuilderformUploaderMessages) : {};

            // Replace defaults if set
            if (this.uploaderData.max_file_size !== null && this.uploaderData.max_file_size <= maxFilesize) {
                maxFilesize = this.uploaderData.max_file_size;
            }

            if (this.uploaderData.allowed_extensions !== '') {
                acceptedFiles = this.uploaderData.allowed_extensions;
            }

            const extractExtensions = (inputString) => {
                const parts = inputString.split(',');
                const extensions = [];

                const extensionRegex = /\/([^/]+)$/; // Matcht alles nach dem letzten Schrägstrich bis zum Ende des Strings

                for (const part of parts) {
                    const match = part.match(extensionRegex);
                    if (match && match[1]) {
                        let extension = match[1];
                        if (extension === 'jpeg') {
                            extension = 'jpg';
                        }
                        extensions.push(extension);
                    }
                }
                return [...new Set(extensions)];
            };

            const formattedAcceptedFiles = extractExtensions(acceptedFiles);

            // Messages
            const uploadeMessage = this.uploaderTranslations.uploadInfo;
            const maxFilesizeMessage = this.uploaderTranslations.maxFilesize.replace('%filesize%', maxFilesize);
            const acceptedFilesMessage = this.uploaderTranslations.acceptedFiles.replace('%formats%', formattedAcceptedFiles.join(', '));

            new DropzoneHandler(this.$form, { // eslint-disable-line
                dropzoneOptions: {
                    dictDefaultMessage: `${uploadeMessage}<small>${acceptedFilesMessage}</small><small>${maxFilesizeMessage}</small>`,
                    maxFilesize,
                    acceptedFiles,
                    init: function () {
                        this.on('error', (file, message) => {
                            hasError = true;
                            programaticallyRemoved = true;
                            formbuilderFormInstance.generateFieldErrors($field, [message]);
                            $field.classList.add('form-field--error');
                        });
                        this.on('success', () => {
                            if (hasError || $field.classList.contains('form-field--error')) {
                                hasError = false;
                                formbuilderFormInstance.removeOldErrorMessages($field);
                            }
                        });
                        this.on('removedfile', () => {
                            if (programaticallyRemoved) {
                                programaticallyRemoved = false;
                                formbuilderFormInstance.removeOldErrorMessages($field);
                            } else if (hasError) {
                                hasError = false;
                                formbuilderFormInstance.generateFieldErrors($field, messages);
                            }
                        });
                    }
                }
            });
        }
    }

    removeOldErrorMessages ($element) {
        const $oldErrorMessages = $element.querySelectorAll('.form-errors');

        if ($oldErrorMessages.length > 0) {
            $element.classList.remove('form-field--error');
            $oldErrorMessages.forEach($oldErrorMessage => {
                $oldErrorMessage.remove();
            });
        }
    }

    createErrorContainer (global = false) {
        const $errorMessages = document.createElement('div');
        $errorMessages.classList.add('form-errors');

        if (global === true) {
            $errorMessages.classList.add('form-errors--grouped');
            $errorMessages.setAttribute('aria-live', 'assertive');
            $errorMessages.setAttribute('tabindex', 0);
            $errorMessages.setAttribute('role', 'alert');
        }

        const $errorList = document.createElement('ul');
        $errorList.classList.add('form-errors__list');

        $errorMessages.appendChild($errorList);

        const errorObject = {};
        errorObject.$errorMessages = $errorMessages;
        errorObject.$errorList = $errorList;

        return errorObject;
    }

    generateFieldErrors ($field, messages) {
        const errorObject = this.createErrorContainer();
        const $errorMessages = errorObject.$errorMessages;
        const $errorList = errorObject.$errorList;

        messages.forEach(message => {
            const $errorItem = document.createElement('li');
            $errorItem.classList.add('form-errors__list-item');
            const iconText = '<span class="icon-error"><svg aria-hidden="true" role="none" class="icon" width="24" height="24"><use xlink:href="#icon-info-outside"></use></svg></span>';
            const errorMessage = iconText + message;
            $errorItem.innerHTML = errorMessage;

            $errorList.appendChild($errorItem);
        });

        $field.appendChild($errorMessages);
    }

    generateFormErrors (messages) {
        const errorObject = this.createErrorContainer(true);
        const $errorMessages = errorObject.$errorMessages;
        const $errorList = errorObject.$errorList;
        const iconText = '<span class="icon-error"><svg aria-hidden="true" role="none" class="icon" width="24" height="24"><use xlink:href="#icon-info-outside"></use></svg></span>';

        $errorMessages.innerHTML = iconText;

        $errorMessages.appendChild($errorList);

        for (const fieldId in messages) {
            if (fieldId === 'general') {
                const $errorItem = document.createElement('li');
                $errorItem.classList.add('form-errors__list-item');
                $errorItem.innerHTML = messages.general;
                $errorList.appendChild($errorItem);
            } else {
                const $input = document.getElementById(fieldId);
                let fieldName = '';

                if ($input) {
                    if ($input.hasAttribute('data-errorname')) {
                        fieldName = $input.getAttribute('data-errorname');
                    } else {
                        const $field = $input.closest('.form-field');
                        const $label = $field.querySelector('label');
                        fieldName = $label.innerText;
                    }

                    const $errorItem = document.createElement('li');
                    $errorItem.classList.add('form-errors__list-item');
                    const $field = '<a href="#' + fieldId + '">' + fieldName + '</a>';

                    $errorItem.innerHTML = formTranslations.fieldError.replace('%fieldname%', $field);

                    $errorList.appendChild($errorItem);
                }
            }
        }

        this.$form.insertBefore($errorMessages, this.$form.firstElementChild);

        smoothScrollTo(0, getOffset(this.$form).top - this.scrollOffset, 400);
    }
}

export { FormBuilder };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $formbuilder = $context.querySelector('[data-formbuilder="root"]');

        if ($formbuilder) {
            const formbuilder = new FormBuilder($formbuilder);
            formbuilder.initialize();
            const $form = $formbuilder.querySelector('form');
            new FriendlyCaptcha($form); // eslint-disable-line
            // new ConditionalLogic($form); // eslint-disable-line
        }
    }
});
